import exifr from "exifr";
import {UploadFile} from "antd/es/upload/interface";
import {AnyElementValue, Nullable} from "@data-force/app";
import heic2any from "heic2any";
import {Result, ResultObject, Track} from "../../types/types";
import {flattenObject} from "../../utils/common";
import {
    EXIF_HEIGHT,
    EXIF_WIDTH,
    HEIC_FILE_EXTENSION,
    HEIC_MIME_TYPE,
} from "../../utils/constants";
import {ExifrInfo, FileTypeAndExtension} from "../../interfaces/store/actions/payload/fileValidation";

declare global {
    // eslint-disable-next-line no-var,vars-on-top
    var MediaInfo: any;
}

const DEFAULT_TRACK: Track = {"@type": "General"};

const getMediaInfoResult = (fileInfo: Nullable<Result>): ResultObject => {
    const result: Result = fileInfo
        ? {...fileInfo as ResultObject}
        : {"@ref": "General", media: {track: [{...DEFAULT_TRACK}]}};
    if (!result.media) {
        result.media = {track: [{...DEFAULT_TRACK}]};
    }
    if (!result.media.track?.length) {
        result.media.track = [{...DEFAULT_TRACK}];
    }
    return result;
};

export const getReadChunkFunc = (file: File): Function => (chunkSize: number, offset: number)
: Promise<Uint8Array> | Uint8Array => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event: ProgressEvent<FileReader>) => {
        if (event.target?.error) {
            reject(event.target.error);
        }
        const buffer: ArrayBuffer = event.target?.result
            ? event.target?.result as ArrayBuffer
            : new ArrayBuffer(0);
        resolve(new Uint8Array(buffer));
    };
    reader.readAsArrayBuffer(file.slice(offset, offset + chunkSize));
});

export const getSizeFunc = (size: number): Function => (): number => size;

const analyzeFileTimeout = (): Promise<Result> => new Promise(() => {
    setTimeout(() => getMediaInfoResult(null), 30000);
});

const analyzeFile = (mediaInfo: any, size: number, file: File): Promise<Result> => mediaInfo
    .analyzeData(getSizeFunc(size), getReadChunkFunc(file));

const analyzeWithMediaInfo = (mediaInfo: any, size: number, file: File): Promise<Result> => Promise
    .race([
        analyzeFile(mediaInfo, size, file),
        analyzeFileTimeout(),
    ])
    .catch(() => getMediaInfoResult(null));

const isHeicFile = (fileTypeAndExtension: FileTypeAndExtension): boolean => {
    const {fileType, fileExtension}: FileTypeAndExtension = fileTypeAndExtension;
    return (fileType === HEIC_MIME_TYPE || fileExtension?.toUpperCase() === HEIC_FILE_EXTENSION);
};

/**
 * This method uses "exifr" library to retrieve overall Width and Height of HEIC file.
 * If "exifr" is not able to retrieve exif data, HEIC is converted to PNG, and Width and Height are retrieved form PNG,
 * using "mediainfo" lib.
 *
 * @param {any} mediaInfo
 * @param {Nullable<Result>} fileInfo
 * @param {Blob | File} originFileObj
 * @param {number} size
 * @return {Promise<Result>}
 */
const handleHeicFile = async (
    mediaInfo: any,
    fileInfo: Nullable<Result>,
    originFileObj: Blob | File,
    size: number,
): Promise<Result> => {
    const result: ResultObject = getMediaInfoResult(fileInfo);
    let width: Nullable<string> = null;
    let height: Nullable<string> = null;

    const heicExif: any = await exifr.parse(originFileObj);
    if (heicExif && !heicExif.errors) {
        width = heicExif.ExifImageWidth;
        height = heicExif.ExifImageHeight;
    } else {
        const blob: Blob = originFileObj || new Blob();
        const heicAsPng: any = await heic2any({ blob });
        const heicAsPngInfo: Nullable<Result> = await analyzeWithMediaInfo(mediaInfo, size, heicAsPng as File);

        if (heicAsPngInfo) {
            const pngTracks: Track[] = (heicAsPngInfo as ResultObject).media.track
                .map((track: Track) => flattenObject(track));
            const widthValues: AnyElementValue[] = pngTracks.map((track: Track) => track[EXIF_WIDTH])
                .filter((val: Nullable<AnyElementValue>) => !!val);
            const heightValues: AnyElementValue[] = pngTracks.map((track: Track) => track[EXIF_HEIGHT])
                .filter((val: Nullable<AnyElementValue>) => !!val);

            if (widthValues.length && heightValues.length) {
                width = widthValues[0] as string;
                height = heightValues[0] as string;
            }
        }
    }

    if (width && height) {
        result.media.track = result.media.track
            .map((track: Track) => ({...track, ...{Width: width!, Height: height!}}));
    }

    return result;
};

/**
 * Adds exif props extracted using exifr, to "General" track of MediaInfo result.
 *
 * @param {ExifrInfo} info
 * @return {Promise<Result>}
 */
const extractExifrData = async (info: ExifrInfo): Promise<Result> => {
    const {fileInfo, originFileObj}: ExifrInfo = info;
    const result: ResultObject = getMediaInfoResult(fileInfo);
    const exifrData: any = await exifr.parse(originFileObj, true);

    if (exifrData && !exifrData.errors) {
        const tracks: Track[] = result.media.track.slice();
        const generalTrackIndex: number = tracks.findIndex(
            (track: Track) => track["@type"] === "General",
        );
        const generalTrack: Track = generalTrackIndex >= 0 ? tracks[generalTrackIndex] : {...DEFAULT_TRACK};

        Object.keys(exifrData).forEach((exifKey: string): void => {
            generalTrack[exifKey] = exifrData[exifKey];
        });

        if (generalTrackIndex >= 0) {
            tracks.splice(generalTrackIndex, 1, generalTrack);
        } else {
            tracks.push(generalTrack);
        }
        result.media.track = tracks;
    }

    return result;
};

/**
 * Using 3rd party MediaInfo and exifr libraries. performs exif analysis of the file.
 *
 * @param {UploadFile} uploadFile
 * @param {FileTypeAndExtension} fileTypeAndExtension
 * @return {Promise<Nullable<Result>>}
 */
export const getFileMediaInfo = async (uploadFile: UploadFile, fileTypeAndExtension: FileTypeAndExtension)
: Promise<Nullable<Result>> => {
    const {originFileObj, size}: UploadFile = uploadFile;

    let result: Nullable<Result> = null;
    if (originFileObj) {
        try {
            // extract exif data using MediaInfo
            const mediaInfo = await window.MediaInfo();
            if (mediaInfo && size) {
                result = await analyzeWithMediaInfo(mediaInfo, size, originFileObj as File);
                if (isHeicFile(fileTypeAndExtension)) {
                    result = await handleHeicFile(mediaInfo, result, originFileObj, size);
                }
            }

            // extract exif data using exifr
            result = await extractExifrData({fileInfo: result, originFileObj});
        } catch (e) {
            // console.log(`error for file: ${uploadFile.name}`, e);
        }
    }
    return result;
};
